import type { ListaPrecosPulado, ListaPrecosQuery, ListaPrecosResponse, ListaPrecosLinha } from '@triade/contracts';
import type { FiltrosLista } from '@/api/lista-precos';

/**
 * Filtros da Lista de precos. Vivem na URL (`validateSearch`): voltar/compartilhar o link preserva a vista.
 * Tudo aqui e apresentacao: filtro, ordem e paginacao rodam no servidor (GET /precificacao/lista).
 */
export interface BuscaLista {
  tabelaId?: string;
  condicaoId?: string;
  data?: string;
  q?: string;
  tipo?: NonNullable<ListaPrecosQuery['tipo']>;
  /** Categoria do item (com as subcategorias) ou `SEM` = sem categoria. */
  categoriaId?: string;
  abaixo?: boolean;
  semPraticado?: boolean;
  semFicha?: boolean;
  ordem?: '-codigo';
}

const ID = /^[A-Za-z0-9_-]{1,64}$/;
const DATA = /^\d{4}-\d{2}-\d{2}$/;
const TIPOS = ['PRODUTO', 'MERCADORIA', 'SERVICO', 'SERVICO_TERCEIRO'] as const;
// A URL `?q=10874` chega do roteador como NUMERO (parse JSON); aceita numero finito como texto.
const texto = (v: unknown): string | undefined => {
  const t = typeof v === 'number' && Number.isFinite(v) ? String(v) : v;
  return typeof t === 'string' && t.trim() ? t.trim().slice(0, 100) : undefined;
};
const ligado = (v: unknown) => v === true || v === 'true';

/** `validateSearch` da rota: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBusca(bruto: Record<string, unknown>): BuscaLista {
  const tabelaId = texto(bruto.tabelaId);
  const condicaoId = texto(bruto.condicaoId);
  const data = texto(bruto.data);
  const q = texto(bruto.q);
  const tipo = TIPOS.find((t) => t === bruto.tipo);
  const categoriaId = texto(bruto.categoriaId);
  return {
    ...(tabelaId && ID.test(tabelaId) ? { tabelaId } : {}),
    ...(condicaoId && ID.test(condicaoId) ? { condicaoId } : {}),
    ...(data && DATA.test(data) ? { data } : {}),
    ...(q ? { q } : {}),
    ...(tipo ? { tipo } : {}),
    ...(categoriaId && ID.test(categoriaId) ? { categoriaId } : {}),
    ...(ligado(bruto.abaixo) ? { abaixo: true as const } : {}),
    ...(ligado(bruto.semPraticado) ? { semPraticado: true as const } : {}),
    ...(ligado(bruto.semFicha) ? { semFicha: true as const } : {}),
    ...(bruto.ordem === '-codigo' ? { ordem: '-codigo' as const } : {}),
  };
}

/** Parametros de GET /precificacao/lista (e de /lista.csv) para a busca da URL. */
/** Filtros atuais da tela como strings, para os relatorios (docs/RELATORIOS.md). `semFicha` vem da busca ja filtrada por custo.ver. */
export function paraRelatorio(b: BuscaLista, semFicha: boolean): Record<string, string | undefined> {
  return {
    data: b.data,
    tabelaId: b.tabelaId,
    condicaoId: b.condicaoId && b.tabelaId ? b.condicaoId : undefined,
    tipo: b.tipo,
    categoriaId: b.categoriaId,
    q: b.q,
    abaixoDoSugerido: b.abaixo ? 'true' : undefined,
    semPraticado: b.semPraticado ? 'true' : undefined,
    semFicha: semFicha ? 'true' : undefined,
  };
}

export function paraConsulta(b: BuscaLista): FiltrosLista {
  return {
    ...(b.tabelaId ? { tabelaId: b.tabelaId } : {}),
    ...(b.condicaoId && b.tabelaId ? { condicaoId: b.condicaoId } : {}),
    ...(b.data ? { data: b.data } : {}),
    ...(b.q ? { q: b.q } : {}),
    ...(b.tipo ? { tipo: b.tipo } : {}),
    ...(b.categoriaId ? { categoriaId: b.categoriaId } : {}),
    ...(b.abaixo ? { abaixoDoSugerido: true } : {}),
    ...(b.semPraticado ? { semPraticado: true } : {}),
    ...(b.semFicha ? { semFicha: true } : {}),
    ordem: b.ordem ?? 'codigo',
  };
}

export function temFiltro(b: BuscaLista): boolean {
  return Boolean(b.q || b.tipo || b.categoriaId || b.abaixo || b.semPraticado || b.semFicha);
}

/** Uma linha da grade: calculada pelo motor, ou pulada (sem custo/tributo) com o motivo da API. */
export type LinhaGrade =
  | { tipo: 'calculada'; chave: string; linha: ListaPrecosLinha }
  | { tipo: 'pulada'; chave: string; pulada: ListaPrecosPulado };

export const chaveDe = (itemId: string, tabelaId: string, condicaoId: string) => `${itemId}|${tabelaId}|${condicaoId}`;

/** Une `itens` e `skipped` de cada pagina na ordem de emissao da API (`posicao`); as paginas ja vem em ordem. */
export function juntarPaginas(paginas: ListaPrecosResponse[]): LinhaGrade[] {
  return paginas.flatMap((p) =>
    [
      ...p.itens.map((linha) => ({ posicao: linha.posicao, l: { tipo: 'calculada', chave: chaveDe(linha.itemId, linha.tabelaId, linha.condicaoId), linha } as LinhaGrade })),
      ...p.skipped.map((pulada) => ({ posicao: pulada.posicao, l: { tipo: 'pulada', chave: `${pulada.itemId}|${pulada.tabelaId}|pulada`, pulada } as LinhaGrade })),
    ]
      .sort((a, b) => a.posicao - b.posicao)
      .map((x) => x.l),
  );
}

export const ROTULO_MOTIVO: Record<ListaPrecosPulado['reason'], string> = {
  UNIT_COST_MISSING: 'Sem custo',
  TAXES_MISSING: 'Sem tributos',
  LUCRO_DESEJADO_AUSENTE: 'Sem lucro desejado',
};
